import {
  BiometricIdentityVerificationRequest,
  BiometricIdentityVerificationRequestStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useEffect } from "react";

import { queriesBiometricIdentityVerificationRequests } from "../../../../../v3/query/queriesBiometricIdentityVerificationRequests";
import { RequirementCardContent } from "../../../components/RequirementCardContent";
import { RequirementTableItemData } from "../../../types";
import { RequirementEvent } from "../../../utils";
import {
  IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES,
  IDENTITY_VERIFICATION_REQUIREMENT_EVENT,
  useIDVPendingSuccessStatus
} from "../configs/utils";

export const ValidationProgress: React.FC<{
  identityVerification: BiometricIdentityVerificationRequest | undefined;
  requirement: RequirementTableItemData;
  onSuccess: (
    event: RequirementEvent,
    requirement: RequirementTableItemData
  ) => void;
}> = ({ requirement, identityVerification, onSuccess }) => {
  const queryIdentityVerification =
    queriesBiometricIdentityVerificationRequests.getBiometricIdentityVerificationRequest.useQuery(
      {
        biometricIdentityVerificationRequestId: identityVerification?.id!
      },
      {
        enabled: !!identityVerification?.id
      }
    );

  const { setPendingSuccessStatus } = useIDVPendingSuccessStatus();

  useEffect(() => {
    // Refetch the verification request while it is still in flight
    if (
      IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES.includes(
        queryIdentityVerification.data?.status
      )
    ) {
      // Refetch the verification every few seconds
      const interval = setInterval(() => {
        // This is to ensure that the status is updated real-time in the UI
        queryIdentityVerification.refetch().then(result => {
          if (
            result.data?.status ===
              BiometricIdentityVerificationRequestStatus.COMPLETE ||
            result.data?.status ===
              BiometricIdentityVerificationRequestStatus.FAILED
          ) {
            clearInterval(interval);
            setPendingSuccessStatus(
              requirement,
              queryIdentityVerification.data
            );
            onSuccess(
              IDENTITY_VERIFICATION_REQUIREMENT_EVENT.CompleteVerification,
              requirement
            );
          }
        });
      }, 3000);
      return () => clearInterval(interval);
    }
    // re-arm only on status change — the other deps are new objects every
    // render and would reset the polling interval
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryIdentityVerification.data?.status]);

  return (
    <RequirementCardContent
      description="This typically takes up to 5 minutes. Your payer will be notified when it is complete."
      title="Your ID verification is being processed"
      type="people"
    />
  );
};
